import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { s } from '@/features/integrations/guideStyles'

// ── Troubleshooting ───────────────────────────────────────────────────────────
type OsTab = 'linux' | 'windows-cmd' | 'windows-ps'

export function TroubleshootingGuide() {
  const [osTab, setOsTab] = useState<OsTab>('linux')
  const osTabs: OsTab[] = ['linux', 'windows-cmd', 'windows-ps']

  return (
    <div>
      <h2 style={s.guideTitle}>Troubleshooting</h2>
      <p style={s.para}>Enable verbose logging to diagnose issues with Terraform or OpenTofu operations.</p>

      <h3 style={s.subTitle}>Enable Debug Logging</h3>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {osTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setOsTab(tab)}
            style={{
              padding: '4px 10px',
              border: `1px solid ${osTab === tab ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 4,
              fontFamily: 'var(--mono)',
              fontSize: '0.7rem',
              cursor: 'pointer',
              background: osTab === tab ? 'var(--accent-d2)' : 'transparent',
              color: osTab === tab ? 'var(--accent)' : 'var(--text2)',
            }}
          >
            {tab === 'linux' ? 'macOS & Linux' : tab === 'windows-cmd' ? 'Windows (CMD)' : 'Windows (PowerShell)'}
          </button>
        ))}
      </div>

      {osTab === 'linux' && (
        <>
          <ShellSnippet code={`# Option 1: Set for a single command
TF_LOG=1 tofu apply

# Option 2: Set for the entire session
export TF_LOG=1
tofu init
tofu plan
tofu apply

# Logs are written to stderr by default`} language="bash" />
          <p style={s.para}>To capture logs to a file:</p>
          <ShellSnippet code={`TF_LOG=1 TF_LOG_PATH=tofu-debug.log tofu apply`} language="bash" />
        </>
      )}

      {osTab === 'windows-cmd' && (
        <ShellSnippet code={`REM Option 1: Set for a single command
set TF_LOG=1
tofu apply

REM Option 2: Capture logs to a file
set TF_LOG=1
set TF_LOG_PATH=tofu-debug.log
tofu apply`} language="cmd" />
      )}

      {osTab === 'windows-ps' && (
        <ShellSnippet code={`# Option 1: Set for a single command
$env:TF_LOG=1
tofu apply

# Option 2: Capture logs to a file
$env:TF_LOG=1
$env:TF_LOG_PATH="tofu-debug.log"
tofu apply`} language="powershell" />
      )}

      <h3 style={s.subTitle}>Log Levels</h3>
      <ul style={s.list}>
        <li><code style={s.code}>TF_LOG=TRACE</code> - Most verbose, includes all debug information</li>
        <li><code style={s.code}>TF_LOG=DEBUG</code> - Detailed debug information</li>
        <li><code style={s.code}>TF_LOG=INFO</code> - General information</li>
        <li><code style={s.code}>TF_LOG=WARN</code> - Warnings and errors only</li>
        <li><code style={s.code}>TF_LOG=ERROR</code> - Errors only</li>
      </ul>

      <h3 style={s.subTitle}>Tips</h3>
      <ul style={s.list}>
        <li>State backend errors will show the exact HTTP requests and responses</li>
        <li>Provider authentication issues are logged with detailed error messages</li>
        <li>Review logs before sharing - they may contain sensitive values</li>
      </ul>
    </div>
  )
}
